<template>
  <div class="video-uploader">
    <!-- 已上传：播放器 + 更换/移除 -->
    <template v-if="value">
      <div class="preview-wrap">
        <video :src="value" class="preview-video" controls preload="metadata" />
      </div>
      <div class="preview-meta">
        <span class="file-name">{{ uploading ? '上传中…' : (fileName || '已上传视频') }}</span>
        <span class="meta-actions">
          <el-upload
            class="inline-upload"
            action="#"
            :show-file-list="false"
            :auto-upload="false"
            accept="video/*"
            :on-change="handleChange"
          >
            <el-button size="mini" icon="el-icon-refresh" :disabled="uploading">更换视频</el-button>
          </el-upload>
          <el-button size="mini" type="text" class="remove-btn" :disabled="uploading" @click="handleRemove">移除</el-button>
        </span>
      </div>
    </template>

    <!-- 未上传：上传区 -->
    <el-upload
      v-else
      class="uploader-box"
      drag
      action="#"
      :show-file-list="false"
      :auto-upload="false"
      accept="video/*"
      :on-change="handleChange"
    >
      <div class="upload-placeholder">
        <i :class="uploading ? 'el-icon-loading' : 'el-icon-video-camera'" />
        <span class="upload-tip">{{ uploading ? '视频上传中，请稍候…' : '点击或将视频拖到此处上传' }}</span>
      </div>
    </el-upload>

    <p class="uploader-hint">
      支持 MP4 / MOV 等常见格式，建议 1080P 以内、单个不超过 200MB
    </p>
  </div>
</template>

<script>
import uploadToQiniu from '@/util/qiniu-upload'

export default {
  name: 'VideoUploader',
  props: {
    value: { type: String, default: '' },
    /** 七牛存储 key 前缀 */
    dir: { type: String, default: 'common/video' },
    /** 预留覆盖：传 raw => Promise<播放地址> 则不走默认七牛直传 */
    upload: { type: Function, default: null }
  },
  data() {
    return {
      fileName: '',
      uploading: false
    }
  },
  methods: {
    handleChange(file) {
      const raw = file.raw
      if (!raw) return
      if (this.uploading) {
        this.$message.warning('视频正在上传中，请稍候')
        return
      }
      // 部分容器（如 .mov）浏览器不给 MIME，退化为后缀判断
      const isVideo = /^video\//.test(raw.type) || /\.(mp4|mov|m4v|webm|ogg|avi)$/i.test(raw.name || '')
      if (!isVideo) {
        this.$message.error('只能上传视频文件')
        return
      }
      if (raw.size / 1024 / 1024 > 200) {
        this.$message.error('视频大小不能超过 200MB')
        return
      }
      this.fileName = raw.name || ''
      this.uploading = true
      const task = typeof this.upload === 'function' ? this.upload(raw) : uploadToQiniu(raw, this.dir)
      task.then(url => {
        this.$emit('input', url)
        this.$emit('change', url)
        // 自动读取时长/宽高，供业务表单回填（帖子/内容的视频时长与横竖版判定）
        this.readDuration(url)
      }).catch(() => {
        this.$message.error('视频上传失败，请重试')
      }).finally(() => {
        this.uploading = false
      })
    },
    handleRemove() {
      this.fileName = ''
      this.$emit('input', '')
      this.$emit('change', '')
      this.$emit('duration', 0)
      this.$emit('metadata', { duration: 0, width: 0, height: 0 })
    },
    /**
     * 读视频元信息（时长/宽高）并抛出 duration、metadata 事件。
     * 表单侧据此自动识别横版/竖版与时长，运营不用手填。
     */
    readDuration(url) {
      if (!url) return
      try {
        const probe = document.createElement('video')
        probe.preload = 'metadata'
        probe.onloadedmetadata = () => {
          const seconds = Math.round(probe.duration || 0)
          if (seconds > 0) this.$emit('duration', seconds)
          this.$emit('metadata', {
            duration: seconds,
            width: probe.videoWidth || 0,
            height: probe.videoHeight || 0
          })
          probe.src = ''
        }
        probe.onerror = () => { probe.src = '' }
        probe.src = url
      } catch (e) {
        // 浏览器不支持 metadata 预读时静默跳过，不影响上传主流程
      }
    }
  }
}
</script>

<style scoped>
.video-uploader {
  width: 100%;
}

.preview-wrap {
  width: 100%;
  max-width: 420px;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  line-height: 0;
}

.preview-video {
  width: 100%;
  max-height: 236px;
  display: block;
}

.preview-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  max-width: 420px;
  margin-top: 8px;
}

.file-name {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
}

.inline-upload {
  display: inline-block;
}

.uploader-box >>> .el-upload {
  width: 100%;
  max-width: 420px;
}

.uploader-box >>> .el-upload-dragger {
  width: 100%;
  padding: 28px 16px;
  border-radius: 8px;
  transition: border-color 0.2s;
}

.uploader-box >>> .el-upload-dragger:hover {
  border-color: var(--primary);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8c939d;
}

.upload-placeholder i {
  font-size: 28px;
}

.upload-tip {
  font-size: 13px;
}

.remove-btn {
  color: #f56c6c;
}

.uploader-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
